import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  KeyRound,
  RefreshCw,
  ShieldCheck,
  X,
} from 'lucide-react';

export interface LicenseCenterRecordItem {
  id: string;
  time: string;
  target: string;
  result: string;
  reward: string;
}

export interface LicenseCenterSnapshot {
  licenseType: string;
  expiresAt: string | null;
  permanent: boolean;
  accountLabel: string;
  accountDetail: string | null;
  cardLimitLabel: string;
  sourceLabel: string;
  stageLabel: string;
  remainingLabel: string;
  deviceId: string;
  fingerprintSummary: string;
  inviteCode: string;
  requestedInviteCode: string | null;
  inviteRule: string;
  inviteProgressLabel: string;
  rewardPreview: string[];
  inviteSlotsText: string;
  pendingRewardCount: number;
  eligibleForInvite: boolean;
  nextRewardLabel: string | null;
  inviteRecords: LicenseCenterRecordItem[];
  rewardRecords: LicenseCenterRecordItem[];
  bootstrapNotice: string | null;
}

interface LicenseCenterDialogProps {
  open: boolean;
  snapshot: LicenseCenterSnapshot;
  busyAction:
    | 'activation'
    | 'reward'
    | 'request-invite'
    | 'claim-reward'
    | null;
  onOpenChange: (open: boolean) => void;
  onCopyInviteCode: () => void;
  onApplyActivationCode: (code: string) => void;
  onApplyRewardCode: (code: string) => void;
  onRequestInviteCode: () => void;
  onClaimInviteReward: () => void;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '未记录';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '未记录';
  }

  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const formatCountdown = (
  targetAt: string | null,
  nowMs: number,
  options?: {
    permanent?: boolean;
    emptyLabel?: string;
  },
): string => {
  if (options?.permanent) {
    return '永久有效';
  }

  if (!targetAt) {
    return options?.emptyLabel ?? '未授权';
  }

  const targetMs = new Date(targetAt).getTime();
  if (Number.isNaN(targetMs)) {
    return '未授权';
  }

  const diff = targetMs - nowMs;
  if (diff <= 0) {
    return '已到期';
  }

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}天 ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const BusyIcon = () => <RefreshCw size={14} className="spin" />;

export function LicenseCenterDialog({
  open,
  snapshot,
  busyAction,
  onOpenChange,
  onApplyActivationCode,
}: LicenseCenterDialogProps) {
  const [activationCode, setActivationCode] = useState('');
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!open) {
      return;
    }

    setActivationCode('');
  }, [open]);

  useEffect(() => {
    if (!open || snapshot.permanent) {
      return;
    }

    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [open, snapshot.permanent]);

  const countdownText = useMemo(
    () =>
      formatCountdown(snapshot.expiresAt, nowMs, {
        permanent: snapshot.permanent,
        emptyLabel: snapshot.remainingLabel || '未授权',
      }),
    [nowMs, snapshot.expiresAt, snapshot.permanent, snapshot.remainingLabel],
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="shell-dialog-overlay" />
        <Dialog.Content className="shell-dialog-panel shell-dialog-panel--license">
          <header className="shell-dialog-header">
            <div>
              <Dialog.Title className="shell-dialog-title">授权中心</Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="shell-dialog-close" aria-label="关闭授权中心">
                <X size={18} />
              </button>
            </Dialog.Close>
          </header>

          <div className="shell-dialog-body shell-dialog-body--license">
            <section className="license-status-card license-status-card--compact">
              <div className="license-status-card__header">
                <div className="license-status-card__icon">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <strong className="license-status-card__title">{snapshot.licenseType}</strong>
                </div>
              </div>

              <div className="license-status-card__metrics license-status-card__metrics--inline">
                <div className="about-metric-card about-metric-card--compact">
                  <span className="about-metric-card__label">当前账号</span>
                  <strong className="about-metric-card__value">{snapshot.accountLabel}</strong>
                  {snapshot.accountDetail ? (
                    <span className="license-metric-subtext">{snapshot.accountDetail}</span>
                  ) : null}
                </div>
                <div className="about-metric-card about-metric-card--compact">
                  <span className="about-metric-card__label">到期时间</span>
                  <strong className="about-metric-card__value">
                    {snapshot.permanent ? '永久有效' : formatDateTime(snapshot.expiresAt)}
                  </strong>
                  {!snapshot.permanent ? (
                    <span className="license-metric-subtext">{countdownText}</span>
                  ) : null}
                </div>
                <div className="about-metric-card about-metric-card--compact">
                  <span className="about-metric-card__label">卡片数量</span>
                  <strong className="about-metric-card__value">{snapshot.cardLimitLabel}</strong>
                </div>
                <div className="about-metric-card about-metric-card--compact">
                  <span className="about-metric-card__label">授权来源</span>
                  <strong className="about-metric-card__value">{snapshot.sourceLabel}</strong>
                </div>
              </div>
            </section>

            <section className="about-section-card license-activation-card">
              <div className="license-inline-form">
                <label htmlFor="license-activation-code" className="license-inline-form__label">
                  激活授权码
                </label>
                <div className="license-inline-form__controls">
                  <input
                    id="license-activation-code"
                    value={activationCode}
                    onChange={(event) => setActivationCode(event.target.value)}
                    placeholder="AVX-XXXX-XXXXXX-XXXXXX-XXXX"
                    className="license-input"
                  />
                  <button
                    type="button"
                    className="sidebar-button license-inline-form__button"
                    onClick={() => onApplyActivationCode(activationCode)}
                  >
                    {busyAction === 'activation' ? <BusyIcon /> : <KeyRound size={14} />}
                    绑定并激活
                  </button>
                </div>
              </div>
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
